/**
 * Screen 3 — paste a job description, see scored candidates.
 *
 * The matching here is SIMULATED and the screen says so in three places: the
 * heading area, the per-result labelling, and the simulation notice beneath.
 * That is not over-caution. The results look exactly like real output, and a
 * prospect has no way to tell the difference from the inside.
 *
 * The mask/unmask mechanic is the one thing on this screen that mirrors real
 * product behaviour, so it is built for real -- but the copy describes a
 * boundary rather than claiming to conceal a specific person's name, because
 * the synthetic records have no name to conceal. See simulation-notice.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal } from 'antd';
import FlowShell from '../../components/flow-shell';
import SimulationNotice from '../../components/simulation-notice';
import { useSelfServe } from '../../context';
import { SAMPLE_JDS } from '../../data/samples';
import {
  MatchResult,
  runMatch,
  simulatedDistanceMiles,
  capitalise,
} from '../../utils/match';
import styles from './index.module.css';

const STAGES = [
  'Reading the job description…',
  'Scoring candidates…',
  'Ranking by fit…',
] as const;

const STAGE_MS = 520;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Trial = () => {
  const navigate = useNavigate();
  const { account } = useSelfServe();

  const [officeLocation, setOfficeLocation] = useState('');
  const [jd, setJd] = useState('');
  const [stage, setStage] = useState<number | null>(null);
  const [result, setResult] = useState<MatchResult | null>(null);
  const [unmaskOpen, setUnmaskOpen] = useState(false);

  const timers = useRef<number[]>([]);
  useEffect(
    () => () => timers.current.forEach((id) => window.clearTimeout(id)),
    [],
  );

  const find = useCallback(() => {
    const text = jd.trim();
    if (!text) return;
    setResult(null);

    // The staged readout is presentation. The match itself is synchronous, so
    // this is a deliberate pause to make the steps legible -- skipped entirely
    // when the visitor has asked for reduced motion.
    if (prefersReducedMotion()) {
      setStage(null);
      setResult(runMatch(text));
      return;
    }

    setStage(0);
    STAGES.forEach((_, i) => {
      const id = window.setTimeout(
        () => {
          if (i + 1 < STAGES.length) {
            setStage(i + 1);
          } else {
            setStage(null);
            setResult(runMatch(text));
          }
        },
        STAGE_MS * (i + 1),
      );
      timers.current.push(id);
    });
  }, [jd]);

  const applySample = (id: string) => {
    const sample = SAMPLE_JDS.find((s) => s.id === id);
    if (!sample) return;
    setJd(sample.text);
    setOfficeLocation(sample.officeLocation);
    setResult(null);
  };

  return (
    <FlowShell
      step={3}
      title="Try it on a real job description"
      subtitle={
        account
          ? `${account.company} — paste a posting below. Results are simulated for the trial.`
          : 'Paste a posting below. Results are simulated for the trial.'
      }
      footer={<SimulationNotice />}
    >
      <div className={styles.form}>
        <label className={styles.label} htmlFor="office-location">
          Office location <span className={styles.optional}>(optional)</span>
        </label>
        <Input
          id="office-location"
          size="large"
          autoComplete="address-level2"
          value={officeLocation}
          onChange={(e) => setOfficeLocation(e.target.value)}
          placeholder="e.g. Framingham, MA"
          className={styles.locationInput}
        />

        <label className={styles.label} htmlFor="jd">
          Job description
        </label>
        <Input.TextArea
          id="jd"
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          rows={9}
          placeholder="Paste a job description here — title, requirements, years of experience, anything you would normally post."
        />

        <div className={styles.samples}>
          <span className={styles.samplesLabel}>Or try a sample:</span>
          {SAMPLE_JDS.map((s) => (
            <Button key={s.id} size="small" onClick={() => applySample(s.id)}>
              {s.label}
            </Button>
          ))}
        </div>

        <Button
          type="primary"
          size="large"
          onClick={find}
          disabled={!jd.trim() || stage !== null}
          className={styles.cta}
        >
          Find candidates
        </Button>
      </div>

      {stage !== null && (
        <p className={styles.stage} aria-live="polite">
          {STAGES[stage]}
        </p>
      )}

      {result && (
        <div className={styles.results}>
          <p className={styles.resultsSummary}>
            <strong>{result.ranked.length}</strong> simulated candidates scored
            against your description
            {officeLocation.trim() ? ` · office: ${officeLocation.trim()}` : ''}
            .
          </p>

          <p className={styles.recognised}>
            {result.signals.skills.length ? (
              <>
                Recognised from your description:{' '}
                <strong>
                  {result.signals.skills.map(capitalise).join(', ')}
                </strong>
                {result.signals.requiredYears
                  ? ` · asks for ${result.signals.requiredYears}+ years`
                  : ''}
              </>
            ) : (
              'No specific skill keywords recognised, so these are ranked by experience instead. A more detailed description gives sharper matches.'
            )}
          </p>

          <ul className={styles.cards}>
            {result.ranked.map(({ candidate, score, matched }) => {
              const chips = matched.length
                ? matched
                : candidate.skills.slice(0, 3);
              const miles = simulatedDistanceMiles(
                candidate,
                officeLocation.trim(),
              );
              return (
                <li key={candidate.id} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div>
                      <h3 className={styles.cardTitle}>
                        {capitalise(candidate.domain)}
                      </h3>
                      <p className={styles.cardMeta}>
                        {candidate.years} years · {candidate.education} ·{' '}
                        <span className={styles.simulatedInline}>
                          ~{miles} miles (simulated)
                        </span>
                      </p>
                    </div>
                    <div
                      className={styles.score}
                      role="img"
                      aria-label={`Simulated fit score ${score} out of 100`}
                    >
                      <span aria-hidden="true">{score}</span>
                    </div>
                  </div>

                  <p className={styles.bio}>{candidate.bio}</p>

                  <ul className={styles.highlights}>
                    {candidate.highlights.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>

                  <div className={styles.chips}>
                    {chips.map((s) => (
                      <span key={s} className={styles.chip}>
                        {capitalise(s)}
                      </span>
                    ))}
                  </div>

                  <Button
                    size="small"
                    onClick={() => setUnmaskOpen(true)}
                    className={styles.unmask}
                  >
                    See full profile
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className={styles.activate}>
        <p>Seen enough? Activate your plan to work with real candidates.</p>
        <Button type="primary" onClick={() => navigate('/selfserve/payment')}>
          Activate your plan
        </Button>
      </div>

      <Modal
        open={unmaskOpen}
        onCancel={() => setUnmaskOpen(false)}
        footer={null}
        title="Full profiles come with an active plan"
      >
        <p>
          In the live product, full candidate profiles — including contact
          details — become visible once a plan is active. That boundary is real
          and it works the same way here.
        </p>
        <p className={styles.modalNote}>
          These trial profiles are fictional, so there is no contact information
          behind this button to show you. Activating a plan is what opens real
          candidates.
        </p>
        <div className={styles.modalActions}>
          <Button type="primary" onClick={() => navigate('/selfserve/payment')}>
            Activate your plan
          </Button>
          <Button type="text" onClick={() => setUnmaskOpen(false)}>
            Keep looking
          </Button>
        </div>
      </Modal>
    </FlowShell>
  );
};

export default Trial;
